import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Modal,
  Steps,
  Button,
  Alert,
  Slider,
  Descriptions,
  Tag,
  Space,
  Typography,
  Card,
  Checkbox,
  message,
} from "antd";
import {
  AimOutlined,
  CompassOutlined,
  EyeOutlined,
  ClearOutlined,
  CheckCircleOutlined,
  ArrowRightOutlined,
  ArrowLeftOutlined,
  SaveOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import {
  type GeoPoint,
  type BoundaryAnalysis,
  analyzeBoundary,
  buildSceneGeoReference,
} from "./boundaryMath";
import { CemeteryBoundaryPickerMap } from "./CemeteryBoundaryPickerMap";
import GlbGeoReferencePreviewCanvas from "../../cemetery-3d/GlbGeoReferencePreviewCanvas";
import {
  createSceneGeoReference,
  listSceneGeoReferences,
  verifySceneGeoReference,
  type SceneGeoReferenceData,
} from "./geoReferenceApi";
import "./modelGeoReferenceModal.css";

const { Text } = Typography;

export interface ModelGeoReferenceModalProps {
  readonly open: boolean;
  readonly cemeteryId: string;
  readonly cemeteryName: string;
  readonly cemeteryCode?: string | undefined;
  readonly initialCenter?: [number, number] | undefined;
  readonly modelUrl?: string | undefined;
  readonly existingGeoReference?: SceneGeoReferenceData | null | undefined;
  readonly onClose: () => void;
  readonly onSuccess?: ((saved: SceneGeoReferenceData) => void) | undefined;
}

/**
 * Sinh 4 toạ độ góc mặc định hình chữ nhật bao quanh toạ độ tâm
 */
function generateDefaultCorners(
  center: [number, number],
  widthM = 60,
  lengthM = 80,
): GeoPoint[] {
  const [lat, lng] = center;
  const halfW = widthM / 2;
  const halfL = lengthM / 2;
  const cosLat = Math.cos((lat * Math.PI) / 180);
  const mLon = 111320.0 * Math.max(0.1, cosLat);
  const mLat = 110574.0;
  const dLon = halfW / mLon;
  const dLat = halfL / mLat;

  // CW order: P1 (Tây Bắc), P2 (Đông Bắc), P3 (Đông Nam), P4 (Tây Nam)
  return [
    { lat: lat + dLat, lng: lng - dLon, lon: lng - dLon },
    { lat: lat + dLat, lng: lng + dLon, lon: lng + dLon },
    { lat: lat - dLat, lng: lng + dLon, lon: lng + dLon },
    { lat: lat - dLat, lng: lng - dLon, lon: lng - dLon },
  ];
}

/**
 * Modal định vị mô hình 3D (ModelGeoReferenceModal)
 * Quy trình 3 bước:
 * Bước 1: Cắm 4 góc ranh giới (P1 - P4)
 * Bước 2: Chọn cạnh mặt tiền & xác định hướng cổng (Outward normal bearing)
 * Bước 3: Đối soát mô hình GLB đè bản đồ vệ tinh (với thanh trượt Opacity) & Lưu cấu hình
 */
export function ModelGeoReferenceModal({
  open,
  cemeteryId,
  cemeteryName,
  cemeteryCode,
  initialCenter = [15.1205, 108.8005],
  modelUrl,
  existingGeoReference,
  onClose,
  onSuccess,
}: ModelGeoReferenceModalProps): React.JSX.Element {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [corners, setCorners] = useState<GeoPoint[]>([]);
  const [frontageEdgeIndex, setFrontageEdgeIndex] = useState<number | null>(null);
  const [analysis, setAnalysis] = useState<BoundaryAnalysis | null>(null);

  // Bước 3: thanh trượt opacity (0.0 -> 1.0, mặc định 0.75)
  const [opacity, setOpacity] = useState<number>(0.75);
  const [autoVerify, setAutoVerify] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [activeGeoReference, setActiveGeoReference] = useState<SceneGeoReferenceData | null>(
    existingGeoReference ?? null,
  );

  // Khởi tạo hoặc nạp lại dữ liệu khi mở Modal
  useEffect(() => {
    if (!open) {
      setCurrentStep(0);
      setCorners([]);
      setFrontageEdgeIndex(null);
      setAnalysis(null);
      setErrorMsg(null);
      return;
    }

    if (existingGeoReference) {
      setActiveGeoReference(existingGeoReference);
    } else {
      void listSceneGeoReferences(cemeteryId)
        .then((res) => {
          const active = res.rows?.find((r) => !r.effectiveTo) ?? null;
          if (active) {
            setActiveGeoReference(active);
          }
        })
        .catch(() => {
          // Bỏ qua lỗi nạp nếu chưa có
        });
    }

    // Nếu đã có tọa độ gốc, khởi tạo 4 góc mặc định quanh đó
    const center = initialCenter;
    const defaultPoints = generateDefaultCorners(center);
    setCorners(defaultPoints);
    setFrontageEdgeIndex(0);

    try {
      const res = analyzeBoundary(defaultPoints, 0);
      setAnalysis(res);
    } catch {
      // Bỏ qua lỗi khởi tạo nếu điểm chưa hợp lệ
    }
  }, [open, cemeteryId, existingGeoReference, initialCenter]);

  // Cập nhật kết quả phân tích mỗi khi 4 góc hoặc cạnh mặt tiền thay đổi
  const handleCornersChange = useCallback(
    (newCorners: GeoPoint[]) => {
      setCorners(newCorners);
      if (newCorners.length >= 3) {
        try {
          const edgeIdx = frontageEdgeIndex ?? 0;
          const res = analyzeBoundary(newCorners, edgeIdx);
          setAnalysis(res);
          setErrorMsg(null);
        } catch (err) {
          setErrorMsg((err as Error).message);
        }
      } else {
        setAnalysis(null);
      }
    },
    [frontageEdgeIndex],
  );

  const handleSelectFrontageEdge = useCallback(
    (index: number) => {
      setFrontageEdgeIndex(index);
      if (corners.length >= 3) {
        try {
          const res = analyzeBoundary(corners, index);
          setAnalysis(res);
          setErrorMsg(null);
        } catch (err) {
          setErrorMsg((err as Error).message);
        }
      }
    },
    [corners],
  );

  const handleResetDefault = useCallback(() => {
    const defaultPoints = generateDefaultCorners(initialCenter);
    setCorners(defaultPoints);
    setFrontageEdgeIndex(0);
    try {
      const res = analyzeBoundary(defaultPoints, 0);
      setAnalysis(res);
      setErrorMsg(null);
    } catch (err) {
      setErrorMsg((err as Error).message);
    }
  }, [initialCenter]);

  const handleClearPoints = useCallback(() => {
    setCorners([]);
    setFrontageEdgeIndex(null);
    setAnalysis(null);
  }, []);

  // Điều hướng bước
  const canGoToStep2 = corners.length === 4 && analysis !== null;
  const canGoToStep3 = canGoToStep2 && frontageEdgeIndex !== null && analysis !== null;

  const nextStep = () => {
    if (currentStep === 0 && canGoToStep2) {
      if (frontageEdgeIndex === null) setFrontageEdgeIndex(0);
      setCurrentStep(1);
    } else if (currentStep === 1 && canGoToStep3) {
      setCurrentStep(2);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep((s) => s - 1);
    }
  };

  // Lưu cấu hình định vị
  const handleSaveGeoReference = async () => {
    if (!analysis) return;
    setSaving(true);
    setErrorMsg(null);

    try {
      const geoConfig = buildSceneGeoReference(analysis);
      const today = new Date().toISOString().split("T")[0] ?? "2026-10-26";
      let effectiveFrom = today;
      const prevRef = existingGeoReference ?? activeGeoReference;
      if (prevRef?.effectiveFrom && prevRef.effectiveFrom >= today) {
        const parts = prevRef.effectiveFrom.split("-").map(Number);
        const y = parts[0] ?? 2026;
        const m = parts[1] ?? 1;
        const d = parts[2] ?? 1;
        const nextDate = new Date(Date.UTC(y, m - 1, d + 1));
        effectiveFrom = nextDate.toISOString().split("T")[0] ?? today;
      }

      // Gửi API tạo SceneGeoReference
      const created = await createSceneGeoReference(cemeteryId, {
        sourceSrid: geoConfig.sourceSrid,
        canonicalSrid: geoConfig.canonicalSrid,
        axisOrder: geoConfig.axisOrder === "LonLat" ? "LON_LAT" : geoConfig.axisOrder,
        originLon: geoConfig.originLon,
        originLat: geoConfig.originLat,
        bearingDeg: Number(geoConfig.bearingDeg.toFixed(4)),
        handedness:
          geoConfig.handedness === "RightHanded"
            ? "RIGHT_HANDED_Y_UP"
            : geoConfig.handedness,
        metersPerSceneUnit: Number(geoConfig.metersPerSceneUnit.toFixed(4)),
        source: "FOUR_CORNERS_GEOREF",
        accuracyM: 0.5,
        effectiveFrom,
      });

      let finalResult = created;

      // Nếu bật tự động xác minh, gọi tiếp API verify
      if (autoVerify) {
        try {
          finalResult = await verifySceneGeoReference(cemeteryId, created.id, {
            expectedVersion: created.version,
          });
        } catch (verifyErr) {
          console.warn("Tự động xác minh thất bại:", verifyErr);
        }
      }

      message.success("Đã lưu và xác minh cấu hình định vị 3D thành công!");
      onSuccess?.(finalResult);
      onClose();
    } catch (err) {
      setErrorMsg((err as Error).message || "Không thể lưu cấu hình định vị 3D");
    } finally {
      setSaving(false);
    }
  };

  // Tính toán nhãn hướng theo góc độ
  const bearingDirectionLabel = useMemo(() => {
    if (!analysis) return "";
    const b = analysis.outwardNormalBearingDeg;
    if (b >= 337.5 || b < 22.5) return "Bắc (0°)";
    if (b >= 22.5 && b < 67.5) return "Đông Bắc";
    if (b >= 67.5 && b < 112.5) return "Đông (90°)";
    if (b >= 112.5 && b < 157.5) return "Đông Nam";
    if (b >= 157.5 && b < 202.5) return "Nam (180°)";
    if (b >= 202.5 && b < 247.5) return "Tây Nam";
    if (b >= 247.5 && b < 292.5) return "Tây (270°)";
    return "Tây Bắc";
  }, [analysis]);

  return (
    <Modal
      className="model-georef-modal"
      title={
        <div className="model-georef-header">
          <div className="model-georef-title">
            <CompassOutlined style={{ color: "var(--ncc-primitive-blue-600, #2563eb)" }} />
            <span>Định vị mô hình 3D ranh giới nghĩa trang</span>
          </div>
          <Space>
            {cemeteryCode && <Tag color="blue">{cemeteryCode}</Tag>}
            {(existingGeoReference ?? activeGeoReference) && (
              <Tag color="orange">Đã có neo: v{(existingGeoReference ?? activeGeoReference)?.versionNo}</Tag>
            )}
            <Text type="secondary" style={{ fontSize: 13 }}>
              {cemeteryName}
            </Text>
          </Space>
        </div>
      }
      open={open}
      onCancel={onClose}
      width={1040}
      footer={null}
      destroyOnClose
    >
      {/* 3 Bước tiến trình */}
      <div className="model-georef-steps-wrapper">
        <Steps
          current={currentStep}
          items={[
            {
              title: "Cắm 4 góc",
              description: "Ranh giới P1 - P4",
              icon: <AimOutlined />,
            },
            {
              title: "Chọn mặt tiền",
              description: "Hướng cổng chính",
              icon: <CompassOutlined />,
            },
            {
              title: "Preview GLB & Lưu",
              description: "Đối soát vệ tinh & Lưu",
              icon: <EyeOutlined />,
            },
          ]}
        />
      </div>

      {errorMsg && (
        <Alert
          type="error"
          showIcon
          message="Lưu ý"
          description={errorMsg}
          style={{ marginBottom: 14 }}
          closable
          onClose={() => setErrorMsg(null)}
        />
      )}

      {/* NỘI DUNG TỪNG BƯỚC */}
      <div className="model-georef-step-body">
        {/* BƯỚC 1: CẮM 4 GÓC */}
        {currentStep === 0 && (
          <>
            <Alert
              type="info"
              showIcon
              message="Bước 1: Cắm và điều chỉnh 4 góc ranh giới nghĩa trang"
              description="Click chuột lên bản đồ vệ tinh để đặt 4 điểm góc P1, P2, P3, P4 theo thứ tự liền kề. Bạn có thể kéo thả marker để vi chỉnh sát ranh giới bờ bao thực tế."
            />

            <div className="model-georef-toolbar">
              <div className="model-georef-points-list">
                <Text strong style={{ marginRight: 6 }}>
                  Mốc toạ độ:
                </Text>
                {corners.map((p, idx) => (
                  <span key={idx} className={`model-georef-point-badge p${idx + 1}`}>
                    P{idx + 1}: {p.lat.toFixed(5)}, {(p.lng ?? p.lon ?? 0).toFixed(5)}
                  </span>
                ))}
                {corners.length === 0 && (
                  <Text type="secondary" italic>
                    Chưa cắm điểm nào. Click trên bản đồ hoặc bấm nút tạo khung mặc định.
                  </Text>
                )}
              </div>

              <Space>
                <Button icon={<ReloadOutlined />} onClick={handleResetDefault}>
                  Tạo khung mặc định
                </Button>
                <Button
                  icon={<ClearOutlined />}
                  danger
                  onClick={handleClearPoints}
                  disabled={corners.length === 0}
                >
                  Xoá điểm
                </Button>
              </Space>
            </div>

            <CemeteryBoundaryPickerMap
              corners={corners}
              onCornersChange={handleCornersChange}
              step={1}
              initialCenter={initialCenter}
              height={420}
            />
          </>
        )}

        {/* BƯỚC 2: CHỌN MẶT TIỀN */}
        {currentStep === 1 && (
          <>
            <Alert
              type="info"
              showIcon
              message="Bước 2: Chọn cạnh mặt tiền & hướng cổng chính"
              description="Click vào cạnh trên bản đồ hoặc danh sách bên dưới. Mũi tên xanh lá sẽ chỉ hướng vuông góc ra ngoài (outward normal) để quay cổng mô hình 3D khớp thực tế."
            />

            <div className="model-georef-edge-grid">
              {analysis?.edges.map((edge) => {
                const isSelected = frontageEdgeIndex === edge.index;
                return (
                  <div
                    key={edge.index}
                    className={`model-georef-edge-card ${isSelected ? "selected" : ""}`}
                    onClick={() => handleSelectFrontageEdge(edge.index)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        handleSelectFrontageEdge(edge.index);
                      }
                    }}
                  >
                    <div className="model-georef-edge-card-header">
                      <span className="model-georef-edge-title">
                        Cạnh {edge.index + 1}: P{edge.index + 1} → P
                        {((edge.index + 1) % 4) + 1}
                      </span>
                      {isSelected ? (
                        <Tag color="success" icon={<CheckCircleOutlined />}>
                          Mặt tiền đã chọn
                        </Tag>
                      ) : (
                        <Tag>Chọn cạnh này</Tag>
                      )}
                    </div>
                    <div className="model-georef-edge-meta">
                      <div>Chiều dài: <strong>{edge.lengthM.toFixed(1)} m</strong></div>
                      <div>Phương vị cạnh: <strong>{edge.bearingDeg.toFixed(1)}°</strong></div>
                    </div>
                  </div>
                );
              })}
            </div>

            {analysis && (
              <Card size="small" style={{ background: "var(--ncc-primitive-slate-50, #f8fafc)" }}>
                <Space orientation="horizontal" size="large" wrap>
                  <div>
                    <Text type="secondary">Bề rộng mặt tiền:</Text>{" "}
                    <strong>{analysis.dimensions.widthM.toFixed(1)} m</strong>
                  </div>
                  <div>
                    <Text type="secondary">Chiều sâu khu đất:</Text>{" "}
                    <strong>{analysis.dimensions.depthM.toFixed(1)} m</strong>
                  </div>
                  <div>
                    <Text type="secondary">Diện tích:</Text>{" "}
                    <strong>{Math.round(analysis.areaM2).toLocaleString("vi-VN")} m²</strong>
                  </div>
                  <div>
                    <Text type="secondary">Hướng cổng (Bearing):</Text>{" "}
                    <Tag color="geekblue">
                      {analysis.outwardNormalBearingDeg.toFixed(1)}° ({bearingDirectionLabel})
                    </Tag>
                  </div>
                </Space>
              </Card>
            )}

            <CemeteryBoundaryPickerMap
              corners={corners}
              frontageEdgeIndex={frontageEdgeIndex}
              onSelectFrontageEdge={handleSelectFrontageEdge}
              step={2}
              initialCenter={initialCenter}
              height={360}
            />
          </>
        )}

        {/* BƯỚC 3: PREVIEW GLB & ĐỐI SOÁT VỆ TINH */}
        {currentStep === 2 && (
          <>
            <Alert
              type="success"
              showIcon
              message="Bước 3: Đối soát mô hình 3D khớp với vệ tinh và lưu toạ độ"
              description="Kéo thanh trượt Opacity bên dưới để điều chỉnh độ mờ mô hình 3D đè lên bản đồ vệ tinh. Kiểm tra mái cổng, tường rào trùng khớp thực địa trước khi lưu."
            />

            {/* Thanh trượt Opacity đối soát GLB */}
            <div className="model-georef-slider-bar">
              <div className="model-georef-slider-label">
                <EyeOutlined />
                <span>Độ mờ mô hình 3D (đối soát vệ tinh):</span>
                <Tag color="cyan" style={{ marginLeft: 6 }}>
                  {Math.round(opacity * 100)}%
                </Tag>
              </div>
              <div className="model-georef-slider-ctrl">
                <Slider
                  min={0}
                  max={100}
                  step={1}
                  value={Math.round(opacity * 100)}
                  onChange={(val) => setOpacity(val / 100)}
                  tooltip={{ formatter: (v) => `${v}%` }}
                />
              </div>
            </div>

            {/* Khung đối soát: Bên trái 3D Model Canvas, bên phải Bản đồ vệ tinh */}
            <div className="model-georef-preview-container">
              {/* Box 1: 3D Preview Canvas */}
              <div className="model-georef-preview-box">
                <span className="model-georef-preview-tag">
                  Mô hình 3D (Xoay: {analysis?.outwardNormalBearingDeg.toFixed(1)}°)
                </span>
                <GlbGeoReferencePreviewCanvas
                  modelUrl={modelUrl}
                  bearingDeg={analysis?.outwardNormalBearingDeg ?? 0}
                  targetDimensions={{
                    widthMeters: analysis?.dimensions.widthM,
                    lengthMeters: analysis?.dimensions.depthM,
                  }}
                  opacity={opacity}
                  height={340}
                  showBoundary
                  showHeadingArrow
                />
              </div>

              {/* Box 2: Vệ tinh thực địa Leaflet */}
              <div className="model-georef-preview-box">
                <span className="model-georef-preview-tag">Bản đồ vệ tinh thực tế</span>
                <CemeteryBoundaryPickerMap
                  corners={corners}
                  frontageEdgeIndex={frontageEdgeIndex}
                  step={3}
                  initialCenter={initialCenter}
                  height={340}
                  readOnly
                />
              </div>
            </div>

            {/* Thông số kỹ thuật chuẩn bị lưu */}
            {analysis && (
              <Descriptions
                bordered
                size="small"
                column={{ xs: 1, sm: 2, md: 3 }}
                title={<span style={{ fontSize: 13, fontWeight: 700 }}>Thông số neo toạ độ Scene</span>}
              >
                <Descriptions.Item label="Gốc toạ độ (Lon, Lat)">
                  {analysis.centroid.lng.toFixed(6)}, {analysis.centroid.lat.toFixed(6)}
                </Descriptions.Item>
                <Descriptions.Item label="Phương vị mặt tiền (Bearing)">
                  {analysis.outwardNormalBearingDeg.toFixed(2)}° ({bearingDirectionLabel})
                </Descriptions.Item>
                <Descriptions.Item label="Hệ quy chiếu">
                  WGS84 (EPSG:4326) · RightHanded Y-up
                </Descriptions.Item>
                <Descriptions.Item label="Kích thước thực địa">
                  {analysis.dimensions.widthM.toFixed(1)} m × {analysis.dimensions.depthM.toFixed(1)} m
                </Descriptions.Item>
                <Descriptions.Item label="Diện tích khu đất">
                  {Math.round(analysis.areaM2).toLocaleString("vi-VN")} m²
                </Descriptions.Item>
                <Descriptions.Item label="Tỉ lệ scale">
                  1.000 (Khớp kích thước thực địa)
                </Descriptions.Item>
              </Descriptions>
            )}

            <div style={{ padding: "4px 0" }}>
              <Checkbox
                checked={autoVerify}
                onChange={(e) => setAutoVerify(e.target.checked)}
              >
                <strong>Tự động kích hoạt trạng thái xác minh (VERIFIED)</strong> ngay sau khi lưu
              </Checkbox>
            </div>
          </>
        )}
      </div>

      {/* FOOTER ACTIONS */}
      <div className="model-georef-footer">
        <div>
          {currentStep > 0 && (
            <Button icon={<ArrowLeftOutlined />} onClick={prevStep}>
              Quay lại
            </Button>
          )}
        </div>

        <Space>
          <Button onClick={onClose}>Hủy bỏ</Button>

          {currentStep < 2 ? (
            <Button
              type="primary"
              icon={<ArrowRightOutlined />}
              onClick={nextStep}
              disabled={currentStep === 0 ? !canGoToStep2 : !canGoToStep3}
            >
              Tiếp tục: {currentStep === 0 ? "Chọn mặt tiền" : "Preview GLB & Lưu"}
            </Button>
          ) : (
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={() => void handleSaveGeoReference()}
              loading={saving}
            >
              Lưu cấu hình định vị
            </Button>
          )}
        </Space>
      </div>
    </Modal>
  );
}

export default ModelGeoReferenceModal;
